import { useCallback, useEffect, useState } from 'react';

import { Box, TextField, Checkbox } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { CheckBoxOutlineBlank, CheckBoxRounded, Search } from '@material-ui/icons';
import { Autocomplete } from '@material-ui/lab';

import SelectorLayout from './SelectorLayout';

import theme from '../../../../../theme';
import { SearchboxElementType } from '../EditRateRecCategoryDrawer';

const useStyles = makeStyles(() => ({
  row: {
    display: 'flex',
    flexDirection: 'row'
  },
  option: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    '& .number': {
      width: '20%'
    },
    '& .description': {
      width: '30%'
    },
    '& .count': {
      width: '20%',
      textAlign: 'center'
    },
    '& .dataCategory': {
      width: '30%'
    },
    '& .checkbox-container': {
      width: '40px',
      height: '37px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }
  },

  checkbox: {
    color: theme.palette.info.dark,
    height: '20px',
    display: 'flex',
    justifyContent: 'flex-end',
    '&.disabled': {
      display: 'none'
    }
  },
  textField: {
    minWidth: '397px',
    '& .MuiInputBase-root': {
      backgroundColor: theme.palette.common.white,
      height: '45px'
    },
    '& .MuiInputBase-adornedStart > span.MuiAutocomplete-tag': {
      paddingLeft: '10px'
    },
    '& .MuiAutocomplete-endAdornment': {
      paddingRight: '18px',
      '& .MuiAutocomplete-popupIndicator': {
        pointerEvents: 'none',
        margin: theme.spacing(0, 0.5),
        transform: 'none'
      }
    }
  }
}));

const customFilterOptions = (options: SearchboxElementType[], { inputValue }: any) => {
  inputValue = inputValue.trim();
  return options.filter(
    (option: SearchboxElementType) =>
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      option.accountNumber?.toString().includes(inputValue) ||
      option.name?.toLowerCase().includes(inputValue.toLowerCase())
  );
};

interface Props {
  [key: string]: any;
  onSelect: (selected: any) => void;
  options: SearchboxElementType[];
  placeholder?: string;
  isLoading?: boolean;
  value: SearchboxElementType[];
  isDisabled?: boolean;
}

const SearchboxSelector = ({
  options,
  onSelect,
  placeholder,
  isLoading = false,
  value,
  isDisabled,
  ...props
}: Props) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<SearchboxElementType[]>(value ?? null);
  const [inputValue, setInputValue] = useState('');

  props.onInputChange = (event: any, value: any, reason: any) => {
    if (event && event.type === 'blur') {
      setInputValue('');
    } else if (reason !== 'reset') {
      setInputValue(value);
    }
  };

  props.inputValue = inputValue;

  useEffect(() => {
    setSelected(value);
  }, [value]);

  const openDropdown = () => {
    if (!isDisabled) {
      setOpen(true);
    }
  };

  const onChange = (event: any, values: any) => {
    setSelected(values);
  };

  const selectHandler = useCallback(() => {
    onSelect(selected);
    setOpen(false);
  }, [onSelect, selected]);

  const cancelHandler = useCallback(() => {
    setSelected(value);
    setOpen(false);
    onSelect(value);
  }, [onSelect, value]);

  const handleCheckboxClick = (option: SearchboxElementType) => (event: React.MouseEvent) => {
    event.stopPropagation();
    setSelected((prevSelected) => {
      if (prevSelected.some((s) => s.name === option.name)) {
        return prevSelected.filter((s) => {
          if (option?.dataCategory !== null && option?.dataCategory === s.dataCategory) {
            return false;
          }

          return s.name !== option.name;
        });
      }

      const toAdd = options.filter((opt) => {
        return option.dataCategory === null
          ? option.name === opt.name && !prevSelected.some((s) => s.name === opt.name)
          : option.dataCategory === opt.dataCategory && !prevSelected.some((s) => s.name === opt.name);
      });
      return [...prevSelected, ...(toAdd.length > 0 ? toAdd : [option])];
    });
  };

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      renderTags={() => null}
      id="searchbox-selector"
      options={options ?? null}
      getOptionLabel={(option) => option.name}
      filterOptions={customFilterOptions}
      open={open}
      getOptionDisabled={(option) => Boolean(option.isDisabled)}
      getOptionSelected={(option, value) => option.name === value.name && option.accountNumber === value.accountNumber}
      renderOption={(option) => (
        <Box key={`${option.name}-${option.accountNumber ?? ''}`} className={classes.option}>
          <Box className="number">{option.accountNumber}</Box>
          <Box className="description">{option.name}</Box>
          <Box className="count">{option.count}</Box>
          <Box className="dataCategory">{option.dataCategory}</Box>
          <Box className="checkbox-container">
            <Checkbox
              checked={selected.some((s) => s.name === option.name && s.accountNumber === option.accountNumber)}
              checkedIcon={<CheckBoxRounded className={classes.checkbox} />}
              icon={<CheckBoxOutlineBlank className={classes.checkbox} />}
              className={`check${option.isDisabled ? ' disabled' : ''}`}
              onClick={handleCheckboxClick(option)}
            />
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          className={classes.textField}
          onClick={openDropdown}
          {...params}
          placeholder={placeholder}
          variant="outlined"
        />
      )}
      PaperComponent={({ children, ...props }) => (
        <SelectorLayout isLoading={isLoading} onClose={cancelHandler} onSelect={selectHandler} {...props}>
          {children}
        </SelectorLayout>
      )}
      popupIcon={<Search />}
      value={selected ?? null}
      disabled={isDisabled}
      onFocus={openDropdown}
      onChange={onChange}
      onBlur={cancelHandler}
      {...props}
    />
  );
};

export default SearchboxSelector;
